import { RiAddCircleLine } from "@itandi/itandi-bb-ui";
import cx from "clsx";
import type { ReactElement, ReactNode } from "react";
import { useState } from "react";
import type { DroppableProvided, DropResult } from "react-beautiful-dnd";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { VendorUserListRow } from "~/components/specifics/vendors/settings/vendor_users/VendorUserListRow";
import { VendorUserModal } from "~/components/specifics/vendors/settings/vendor_users/VendorUserModal";
import { useIsItandi } from "~/context/VendorSession";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useVendorUsers } from "~/hooks/vendors/useVendorUsers";
import type { VendorUser } from "~/models/VendorUser";
import { updateSeq } from "~/utils/api/vendors/VendorUser";
import Styles from "./VendorUserList.module.scss";

export function VendorUserList() {
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const canEditSlackMemberId = useIsItandi();
	const { data: vendorUsers, mutate } = useVendorUsers().index();

	// 並び替えしたときにmutate前に画面に反映するためのstate
	const [optimisticOrder, setOptimisticOrder] = useState<VendorUser[] | null>(
		null
	);
	const orderedVendorUsers = optimisticOrder ?? vendorUsers ?? [];

	const handleDragEnd = async (result: DropResult): Promise<void> => {
		const { destination, source } = result;
		if (!destination) {
			return;
		}
		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		) {
			return;
		}
		const reordered = [...orderedVendorUsers];
		const [movedVendorUser] = reordered.splice(source.index, 1);
		reordered.splice(destination.index, 0, movedVendorUser);
		const newVendorUsers = reordered.map(
			(vendorUser: VendorUser, index: number) => ({
				...vendorUser,
				seq: index + 1
			})
		);
		setOptimisticOrder(newVendorUsers);
		try {
			await updateSeq(newVendorUsers);
			await mutate();
		} finally {
			setOptimisticOrder(null);
		}
	};
	const renderRow = (vendorUser: VendorUser, index: number): ReactNode => (
		<VendorUserListRow
			index={index}
			key={vendorUser.id}
			vendorUser={vendorUser}
		/>
	);
	return (
		<div>
			<table className={Styles.List}>
				<thead>
					<tr className={cx(Styles.ListRow, Styles.Header)}>
						<th className={Styles.OrderHandle}>
							<span>並替</span>
						</th>
						<th className={Styles.SpCol}>担当者</th>
						<th className={Styles.PcCol}>担当者名</th>
						<th className={Styles.PcCol}>メールアドレス</th>
						<th className={Styles.PcCol}>電話番号</th>
						{canEditSlackMemberId ? (
							<th className={Styles.PcCol}>Slack ID</th>
						) : null}
						<th>操作</th>
					</tr>
				</thead>
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="droppable">
						{(provided: DroppableProvided): ReactElement<HTMLElement> => (
							<tbody ref={provided.innerRef} {...provided.droppableProps}>
								{orderedVendorUsers.map(renderRow)}
								{provided.placeholder}
							</tbody>
						)}
					</Droppable>
				</DragDropContext>
			</table>
			<div>
				<a className={Styles.AddLink} href="#" onClick={handleOpen}>
					<RiAddCircleLine className={Styles.AddIcon} />
					担当者を追加
				</a>
				{isOpen ? <VendorUserModal onClose={handleClose} /> : null}
			</div>
		</div>
	);
}
